/* Schemes screens */
const { useState: useStateS, useMemo: useMemoS } = React;

function SchemesPage({ go }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [filter, setFilter] = useStateS('all');
  const [q, setQ] = useStateS('');
  const [showCreate, setShowCreate] = useStateS(false);
  // `asOf` shifts the whole page to that point in time — balances, matured,
  // non-matured, member count are all computed against it server-side. Local
  // list state (not QH_DATA.schemes) so a temporary date exploration doesn't
  // clobber the store other screens share.
  const [asOf, setAsOf] = useStateS(todayIso);
  const [schemes, setSchemes] = useStateS(QH_DATA.schemes || []);
  const [loading, setLoading] = useStateS(false);
  const isToday = asOf === todayIso;

  const load = React.useCallback(async () => {
    if (!asOf || asOf.length !== 10) return;
    setLoading(true);
    try {
      const rows = await window.QH_API.get(`/schemes?asOf=${asOf}`);
      setSchemes((rows || []).map(s => ({
        id: s.id, code: s.code, name: s.name,
        members: s.members, totalAmount: s.totalAmount,
        matured: s.matured, nonMatured: s.nonMatured,
        created: s.created, status: s.status, desc: s.description,
      })));
    } catch (e) { console.error('schemes as-of load failed', e); }
    finally { setLoading(false); }
  }, [asOf]);

  React.useEffect(() => { load(); }, [load]);

  const filtered = schemes.filter(s => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (q && !`${s.name} ${s.code}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Schemes</span></div>
          <h1>Savings Schemes</h1>
          <div className="subtitle">
            {filtered.length} schemes · {schemes.reduce((s, x) => s + x.members, 0)} members
            {!isToday && <> · <span style={{color:'var(--accent)'}}>as of {window.fmtDate(asOf)}</span></>}
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={load} title="Reload data" disabled={loading}><I.Refresh/></button>
          <button className="btn" onClick={() => window.safeDownload('/schemes/export.csv', 'schemes.csv')}><I.Download/> Export</button>
          {window.can('schemes','create') && <button className="btn primary" onClick={() => setShowCreate(true)}><I.Plus/> New scheme</button>}
        </div>
      </div>
      {showCreate && <SchemeCreateModal onClose={() => setShowCreate(false)}/>}

      <div className="toolbar">
        <div style={{display: 'flex', gap: 4}}>
          {[['all', 'All'], ['active', 'Active'], ['closed', 'Closed']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`btn sm ${filter === k ? 'accent' : 'ghost'}`}>{l}</button>
          ))}
        </div>
        <div className="sep"/>
        <input className="input" style={{height: 28, width: 220}} placeholder="Search schemes…" value={q} onChange={(e) => setQ(e.target.value)}/>
        <div className="sep"/>
        <label className="text-xs muted" style={{whiteSpace:'nowrap'}}>Balances as of</label>
        <input className="input font-mono" style={{height:28, width:140}} type="date"
               value={asOf} onChange={e => setAsOf(e.target.value)}
               title="Recompute every scheme's balance / matured / non-matured on this date"/>
        {!isToday && <button className="btn sm ghost" onClick={() => setAsOf(todayIso)} title="Reset to today">Today</button>}
        <div className="grow"/>
        <button className="btn sm ghost"><I.Filter/> More filters</button>
      </div>

      <div className="page-body">
        <div className="table-wrap">
          <table className="data">
            <thead><tr>
              <th>Scheme</th><th>Created</th><th className="right">Members</th>
              <th className="right">Total · BHD</th><th className="right">Matured</th><th className="right">Non-matured</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="clickable" onClick={() => go('scheme:' + s.id)}>
                  <td>
                    <div style={{fontWeight: 500}}>{s.name}</div>
                    <div className="text-xs muted font-mono">{s.code}</div>
                  </td>
                  <td className="font-mono text-xs">{window.fmtDate(s.created)}</td>
                  <td className="num right">{s.members}</td>
                  <td className="num right" style={{fontWeight: 600}}>{fmtBHD(s.totalAmount)}</td>
                  <td className="num right pos">{fmtBHD(s.matured)}</td>
                  <td className="num right">{fmtBHD(s.nonMatured)}</td>
                  <td><span className={`tag ${s.status === 'active' ? 'pos' : ''}`}>{s.status}</span></td>
                  <td><I.ChevronRight s={12}/></td>
                </tr>
              ))}
            </tbody>
            {filtered.length > 0 && (() => {
              // Sums honour the current filter + search so operators can see
              // subtotals for whatever slice they're looking at. Members is
              // summed per-scheme, matching the subtitle's "701 members"
              // convention (double-counts members in multiple schemes).
              const t = filtered.reduce((a, s) => ({
                members: a.members + (s.members || 0),
                totalAmount: a.totalAmount + (s.totalAmount || 0),
                matured: a.matured + (s.matured || 0),
                nonMatured: a.nonMatured + (s.nonMatured || 0),
              }), { members: 0, totalAmount: 0, matured: 0, nonMatured: 0 });
              return (
                <tfoot>
                  <tr style={{background:'var(--bg-sunken)',borderTop:'2px solid var(--line)'}}>
                    <td colSpan={2} style={{fontWeight:700,textTransform:'uppercase',fontSize:11,letterSpacing:0.4}}>
                      Total · {filtered.length} scheme{filtered.length === 1 ? '' : 's'}
                    </td>
                    <td className="num right" style={{fontWeight:700}}>{t.members}</td>
                    <td className="num right" style={{fontWeight:700,fontSize:14}}>{fmtBHD(t.totalAmount)}</td>
                    <td className="num right pos" style={{fontWeight:700}}>{fmtBHD(t.matured)}</td>
                    <td className="num right" style={{fontWeight:700}}>{fmtBHD(t.nonMatured)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      </div>
    </>
  );
}

function SchemeDetailPage({ schemeId, go, openTxModal }) {
  const s = QH_DATA.schemes.find(x => x.id === schemeId);
  const [tab, setTab] = useStateS('overview');
  const [showAddEvent, setShowAddEvent] = useStateS(false);
  if (!s) return <div className="page-body">Not found.</div>;

  const events = QH_DATA.events.filter(e => e.schemeId === schemeId);
  const txs = QH_DATA.transactions.filter(t => t.schemeId === schemeId);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <a onClick={() => go('schemes')}>Schemes</a><I.ChevronRight s={10}/><span>{s.code}</span>
          </div>
          <h1>{s.name}</h1>
          <div className="subtitle">{s.desc}</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => go('templates')}><I.MessageSquare s={12}/> Templates</button>
          {window.can('events','create') && <button className="btn" onClick={() => setShowAddEvent(true)}><I.Calendar s={12}/> Add event</button>}
          {window.can('transactions','create') && <button className="btn primary" onClick={() => openTxModal(schemeId)}><I.Plus/> Record transaction</button>}
        </div>
      </div>
      {showAddEvent && <EventCreateModal onClose={() => setShowAddEvent(false)} defaultSchemeId={schemeId}/>}
      <dl className="meta-row">
        <div><dt>Created</dt><dd className="font-mono">{window.fmtDate(s.created)}</dd></div>
        <div><dt>Status</dt><dd><span className="tag pos">{s.status}</span></dd></div>
        <div><dt>Members</dt><dd className="font-mono">{s.members}</dd></div>
        <div><dt>Operators</dt><dd>3 assigned</dd></div>
      </dl>
      <div className="tabs">
        {[['overview', 'Overview'], ['events', 'Events', events.length], ['transactions', 'Transactions', txs.length], ['maturity', 'Maturity tracker'], ['templates', 'Notifications'], ['settings', 'Settings']].map(([k, l, c]) => (
          <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}{c != null && <span className="count">{c}</span>}</button>
        ))}
      </div>
      <div className="page-body">
        {tab === 'overview' && <SchemeOverview s={s} events={events} txs={txs} go={go}/>}
        {tab === 'events' && <EventsList events={events} schemeId={schemeId} go={go} onChanged={() => window.QH_STORE.reload?.()}/>}
        {tab === 'transactions' && <TransactionsTable txs={txs} compact/>}
        {tab === 'maturity' && <MaturityTracker txs={txs.filter(t => t.maturity)}/>}
        {tab === 'templates' && <SchemeTemplatesView/>}
        {tab === 'settings' && <SchemeSettings s={s}/>}
      </div>
    </>
  );
}

function SchemeOverview({ s, events, txs, go }) {
  // Newest first — events arrive from the API ordered DESC by date, but
  // we sort here defensively so the user always sees the most recent at
  // the top of page 1 (matches their expectation: "what just happened?").
  const sortedEvents = React.useMemo(
    () => [...events].sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    [events]
  );
  const PAGE_SIZE = 5;
  const [evPage, setEvPage] = useStateS(0);
  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / PAGE_SIZE));
  // Clamp when the underlying list shrinks (e.g. after a delete).
  const safePage = Math.min(evPage, totalPages - 1);
  const pageEvents = sortedEvents.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  // Day / month / year split for the date column. Using en-GB for the
  // month so it surfaces as a 3-letter abbreviation (MAY, JUN, …) the
  // same way the inline data tables format dates.
  const fmtEventDate = (d) => {
    if (!d) return { day: '—', month: '', year: '' };
    const dt = new Date(d);
    if (isNaN(dt)) return { day: d, month: '', year: '' };
    return {
      day: String(dt.getDate()),
      month: dt.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase(),
      year: String(dt.getFullYear()),
    };
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
      <KPIStrip items={[
        { label: 'Total scheme amount', value: fmtBHD(s.totalAmount) + ' BHD' },
        { label: 'Matured', value: fmtBHD(s.matured) + ' BHD', sub: s.totalAmount ? `${Math.round(s.matured / s.totalAmount * 100)}% of total` : '—' },
        { label: 'Non-matured', value: fmtBHD(s.nonMatured) + ' BHD' },
        { label: 'Other balances', value: fmtBHD(0) + ' BHD' },
      ]}/>
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
        <div className="card">
          <div className="card-header">Composition</div>
          <div className="card-body">
            <div className="bar" style={{height: 14, marginBottom: 10}}>
              <div className="bar-seg" style={{width: `${s.matured / s.totalAmount * 100}%`, background: 'var(--pos)'}}/>
              <div className="bar-seg" style={{width: `${s.nonMatured / s.totalAmount * 100}%`, background: 'var(--accent)'}}/>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-3)'}}>
              <span>● Matured · {fmtBHD(s.matured)}</span>
              <span>● Non-matured · {fmtBHD(s.nonMatured)}</span>
            </div>
            <div className="divider"/>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12}}>
              <div><div className="muted text-xs uppercase">Avg deposit</div><div className="font-mono" style={{fontWeight: 600, fontSize: 14}}>948.220 BHD</div></div>
              <div><div className="muted text-xs uppercase">Last activity</div><div className="font-mono" style={{fontSize: 14}}>2026-04-28</div></div>
              <div><div className="muted text-xs uppercase">Next maturity</div><div className="font-mono" style={{fontSize: 14}}>2026-05-12</div></div>
              <div><div className="muted text-xs uppercase">Banks used</div><div style={{fontSize: 14}}>4</div></div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            Events
            <span className="actions">
              <span className="tag">{sortedEvents.length}</span>
              {totalPages > 1 && (
                <>
                  <button className="icon-btn" disabled={safePage === 0} onClick={() => setEvPage(p => Math.max(0, p - 1))} title="Previous page" style={{height:22,width:22}}>
                    <I.ChevronRight s={11} style={{transform:'rotate(180deg)'}}/>
                  </button>
                  <span className="text-xs muted" style={{minWidth:50, textAlign:'center', fontVariantNumeric:'tabular-nums'}}>
                    {safePage + 1} / {totalPages}
                  </span>
                  <button className="icon-btn" disabled={safePage >= totalPages - 1} onClick={() => setEvPage(p => Math.min(totalPages - 1, p + 1))} title="Next page" style={{height:22,width:22}}>
                    <I.ChevronRight s={11}/>
                  </button>
                </>
              )}
            </span>
          </div>
          {/* Fixed-height body so the card height stays constant across pages —
              page 1 (5 events) and page N (1 event) shouldn't reflow the
              rest of the Overview. Padding sits inside an inner div so the
              outer card-body's `padding: 0` plays nicely with the empty
              state. */}
          <div className="card-body" style={{padding: 0, minHeight: 60 * 5}}>
            {pageEvents.length === 0 ? (
              <div style={{padding:'24px 14px',textAlign:'center',color:'var(--ink-3)',fontSize:12}}>No events yet.</div>
            ) : pageEvents.map(e => {
              const d = fmtEventDate(e.date);
              return (
                <div key={e.id} className="clickable"
                     onClick={() => go && go('event:' + e.id)}
                     style={{padding: '12px 14px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12, cursor: go ? 'pointer' : 'default'}}>
                  <div style={{width: 52, textAlign: 'center'}}>
                    <div className="font-mono" style={{fontSize: 18, fontWeight: 600, lineHeight: 1}}>{d.day}</div>
                    <div className="text-xs muted uppercase" style={{marginTop: 2}}>{d.month}</div>
                    <div className="text-xs muted font-mono" style={{fontSize: 10, marginTop: 1}}>{d.year}</div>
                  </div>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={{fontSize: 13, fontWeight: 500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{e.name}</div>
                    <div className="text-xs muted">{e.transactions} transactions · +{fmtBHD(e.deposits)} / -{fmtBHD(e.withdrawals)}</div>
                  </div>
                  <I.ChevronRight s={12}/>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">Recent transactions</div>
        <div className="table-wrap" style={{border: 'none', borderRadius: 0}}>
          <TransactionsTable txs={txs.slice(0, 6)} compact noWrap/>
        </div>
      </div>
    </div>
  );
}

function EventsList({ events, go, onChanged }) {
  const isAdmin = window.QH_DATA?.activeRole?.id === 'admin';
  const [busyId, setBusyId] = useStateS(null);
  const onDelete = async (e) => {
    // Pre-empt the server-side 409 with a friendly client-side warning
    // when we can already see the event has transactions. Server still
    // re-checks — this is just UX so the user gets the explanation
    // before clicking confirm.
    if ((e.transactions || 0) > 0) {
      alert(`Can't delete "${e.name}" — it has ${e.transactions} transaction(s).\n\nDelete or move them first.`);
      return;
    }
    if (!window.confirm(`Delete event "${e.name}"? This can't be undone.`)) return;
    setBusyId(e.id);
    try {
      await window.QH_API.delete(`/events/${e.id}`);
      await window.QH_STORE.reload?.();
      onChanged?.();
    } catch (err) {
      // Server returns 409 with { error } when transactions exist —
      // surface whatever message it provides so the operator knows
      // which guard tripped.
      alert(err.message || 'Delete failed.');
    } finally { setBusyId(null); }
  };
  return (
    <div className="table-wrap">
      <table className="data">
        <thead><tr><th>Event</th><th>Date</th><th>Status</th><th className="right">Transactions</th><th className="right">Deposits</th><th className="right">Withdrawals</th><th className="right">Net · BHD</th><th></th></tr></thead>
        <tbody>
          {events.map(e => {
            const closed = e.status === 'closed';
            const hasTx = (e.transactions || 0) > 0;
            return (
              <tr key={e.id} className="clickable" onClick={() => go && go('event:' + e.id)}>
                <td><div style={{fontWeight: 500}}>{e.name}</div><div className="text-xs muted font-mono">{e.id.toUpperCase()}</div></td>
                <td className="font-mono text-xs">{window.fmtDate(e.date)}</td>
                <td><span className={`tag ${closed ? '' : 'pos'}`}>{closed ? 'closed' : 'open'}</span></td>
                <td className="num right">{e.transactions}</td>
                <td className="num right pos">{fmtBHD(e.deposits)}</td>
                <td className="num right neg">{fmtBHD(e.withdrawals)}</td>
                <td className="num right" style={{fontWeight: 600}}>{fmtBHD(e.deposits - e.withdrawals)}</td>
                <td style={{whiteSpace:'nowrap',textAlign:'right'}} onClick={ev => ev.stopPropagation()}>
                  {isAdmin && (
                    <button className="icon-btn"
                            title={hasTx ? `Can't delete — ${e.transactions} transaction(s) in this event` : 'Delete event (admin only)'}
                            onClick={() => onDelete(e)}
                            disabled={busyId === e.id}
                            style={{color: hasTx ? 'var(--ink-3)' : 'var(--neg)'}}>
                      <I.Trash s={12}/>
                    </button>
                  )}
                  <I.ChevronRight s={12}/>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTable({ txs, compact, noWrap }) {
  return (
    <div className={noWrap ? '' : 'table-wrap'}>
      <table className="data">
        <thead><tr><th>Ref</th><th>Date</th>{!compact && <th>Scheme</th>}<th>Member</th><th>Type</th><th className="right">Amount · BHD</th><th>Bank</th><th>Maturity</th><th>Status</th></tr></thead>
        <tbody>
          {txs.map(t => {
            const sch = QH_DATA.schemes.find(x => x.id === t.schemeId);
            return (
              <tr key={t.id} className="clickable">
                <td className="font-mono text-xs">{t.id.toUpperCase()}</td>
                <td className="font-mono text-xs">{window.fmtDateTime(t.date)}</td>
                {!compact && <td>{sch?.code}</td>}
                <td>{t.member}</td>
                <td><span className={`tag ${t.type === 'deposit' ? 'pos' : 'neg'}`}>{t.type === 'deposit' ? <I.ArrowDown s={9}/> : <I.ArrowUp s={9}/>}{t.type}</span></td>
                <td className="num right" style={{fontWeight: 600, color: t.type === 'deposit' ? 'var(--pos)' : 'var(--neg)'}}>{t.type === 'withdrawal' ? '−' : '+'}{fmtBHD(t.amount)}</td>
                <td className="text-xs">{t.bank}</td>
                <td className="font-mono text-xs muted">{window.fmtDate(t.maturity) || '—'}</td>
                <td><span className={`tag ${t.status === 'cleared' ? 'pos' : 'warn'}`}>{t.status}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MaturityTracker({ txs }) {
  const sorted = [...txs].sort((a, b) => a.maturity?.localeCompare(b.maturity));
  return (
    <div className="card">
      <div className="card-header">Maturity schedule · per-deposit tracking <span className="actions"><span className="tag info">Each deposit matures independently</span></span></div>
      <div className="card-body">
        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          {sorted.slice(0, 8).map(t => {
            const today = new Date('2026-04-30');
            const mat = new Date(t.maturity);
            const total = mat - new Date(t.date);
            const elapsed = today - new Date(t.date);
            const pct = Math.max(0, Math.min(100, (elapsed / total) * 100));
            const isMature = today >= mat;
            return (
              <div key={t.id} style={{display: 'grid', gridTemplateColumns: '180px 1fr 120px 100px 80px', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--line)'}}>
                <div>
                  <div style={{fontSize: 13, fontWeight: 500}}>{t.member}</div>
                  <div className="text-xs muted font-mono">{t.id.toUpperCase()}</div>
                </div>
                <div>
                  <div className="bar" style={{height: 6}}>
                    <div className="bar-seg" style={{width: pct + '%', background: isMature ? 'var(--pos)' : 'var(--accent)'}}/>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--ink-3)'}}>
                    <span className="font-mono">{window.fmtDate(t.date)}</span>
                    <span className="font-mono">{window.fmtDate(t.maturity)}</span>
                  </div>
                </div>
                <div className="font-mono right text-sm" style={{textAlign: 'right', fontWeight: 600}}>{fmtBHD(t.amount)}</div>
                <div className="text-xs muted right" style={{textAlign: 'right'}}>{Math.round(pct)}%</div>
                <div>{isMature ? <span className="tag pos">Matured</span> : <span className="tag warn">Locked</span>}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SchemeTemplatesView() {
  return (
    <div className="card">
      <div className="card-header">Notification templates · used for this scheme</div>
      <div className="card-body" style={{padding: 0}}>
        {QH_DATA.templates.slice(0, 3).map(t => (
          <div key={t.id} style={{padding: 14, borderBottom: '1px solid var(--line)'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
              <div style={{fontWeight: 500}}>{t.name}</div>
              <span className="tag info">{t.channel}</span>
              <span className="muted text-xs" style={{marginLeft: 'auto'}}>{t.scope}</span>
            </div>
            <div style={{padding: 10, background: 'var(--bg-sunken)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--ink-2)', lineHeight: 1.6}}>{t.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SchemeSettings({ s }) {
  const [name, setName] = useStateS(s.name);
  const [code, setCode] = useStateS(s.code);
  const [desc, setDesc] = useStateS(s.desc);
  const [busy, setBusy] = useStateS(false);

  const save = async () => {
    setBusy(true);
    try {
      await window.QH_API.put(`/schemes/${s.id}`, { name, code, description: desc });
      await window.QH_STORE.reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const close = async () => {
    if (!window.confirm('Close this scheme?')) return;
    setBusy(true);
    try {
      await window.QH_API.post(`/schemes/${s.id}/close`);
      await window.QH_STORE.reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="card">
      <div className="card-header">Configuration</div>
      <div className="card-body" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 700}}>
        <div className="field"><label>Scheme name</label><input className="input" value={name} onChange={e => setName(e.target.value)}/></div>
        <div className="field"><label>Code</label><input className="input font-mono" value={code} onChange={e => setCode(e.target.value)}/></div>
        <div className="field" style={{gridColumn: '1 / -1'}}><label>Description</label><textarea className="textarea" value={desc} onChange={e => setDesc(e.target.value)}/></div>
        <div style={{gridColumn: '1 / -1', fontSize: 12, color: 'var(--muted)'}}>Maturity is now per-transaction — each deposit picks Normal or Commitment with its own months when recorded.</div>
        <div style={{gridColumn: '1 / -1', display: 'flex', gap: 8, marginTop: 8}}>
          <button className="btn primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
          <button className="btn ghost">Cancel</button>
          <button className="btn danger" style={{marginLeft: 'auto'}} disabled={busy || s.status === 'closed'} onClick={close}>{s.status === 'closed' ? 'Closed' : 'Close scheme'}</button>
        </div>
      </div>
    </div>
  );
}

window.SchemesPage = SchemesPage;
window.SchemeDetailPage = SchemeDetailPage;
window.TransactionsTable = TransactionsTable;
